import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  Kanban,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Sun,
  Moon,
  Shield,
  BarChart3,
  Radar,
  FileSearch,
  Columns3,
  Bell,
  BookUser,
  Brain,
  Crosshair,
  Droplets,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTheme } from "@/context/ThemeContext";
import { useAlerts } from "@/context/AlertContext";
import AlertsDropdown from "@/components/AlertsDropdown";
import WellnessPanel from "@/components/WellnessPanel";
import SnippetsPanel from "@/components/SnippetsPanel";

// v4.4.12 — orden del menú optimizado para workflow comercial:
// 1. Dashboard (vista global)
// 2. Leads + 3. Oportunidades (fuentes de negocio, arriba)
// 4. Pipeline (gestión de leads)
// 5. Licitaciones + 6. Pipeline Licita (flujo de licitación con partners)
// 7. Contactos (directorio, apoyo)
// 8. Reportes
// 9. Admin
const navItems = [
  { name: "Mi Trabajo", path: "/mi-trabajo", icon: Briefcase },
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { name: "Leads", path: "/leads", icon: Users },
  { name: "Oportunidades", path: "/oportunidades", icon: Radar },
  { name: "Pipeline", path: "/pipeline", icon: Kanban },
  { name: "Licitaciones", path: "/licitaciones", icon: FileSearch },
  { name: "Pipeline Licita", path: "/pipeline-licitaciones", icon: Columns3 },
  { name: "Contactos", path: "/contactos", icon: BookUser },
  { name: "Inteligencia", path: "/intel", icon: Brain },
  { name: "Radar de Dolor", path: "/radar-dolor", icon: Crosshair },
  { name: "IA Hídrica", path: "/inteligencia-hidrica", icon: Droplets },
  { name: "Reportes", path: "/reports", icon: BarChart3 },
];

const adminNavItems = [
  { name: "Admin", path: "/admin", icon: Shield },
];

export default function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { alertCount, toggleDropdown } = useAlerts();
  const { logout } = useAuth();

  // Combine nav items based on user role
  const allNavItems = user?.role === "admin"
    ? [...navItems, ...adminNavItems]
    : navItems;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.error("Logout error:", e);
    }
    navigate("/login", { replace: true });
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    return (
      <button
        onClick={() => {
          navigate(item.path);
          setSidebarOpen(false);
        }}
        data-testid={`nav-${item.name.toLowerCase()}`}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-normal ease-spring group ${isActive
          ? "bg-gradient-to-r from-teal-400/10 to-blue-600/10 text-teal-400 border-l-[3px] border-teal-400"
          : "theme-text-secondary hover:text-teal-400 hover:bg-teal-400/10"
          }`}
      >
        <Icon className={`w-5 h-5 ${isActive ? "text-teal-400" : "group-hover:text-teal-400"}`} />
        <span className="font-medium">{item.name}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </button>
    );
  };

  return (
    <div className="min-h-screen theme-bg flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50 w-64 theme-bg-tertiary backdrop-blur-xl border-r transform transition-transform duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        style={{ borderColor: 'var(--theme-border)' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b" style={{ borderColor: 'var(--theme-border)' }}>
            <div className="flex items-center gap-3">
              <img
                src="/logo-light.png"
                alt="System Rapid Solutions"
                className="h-8 w-auto"
              />
              <span className="font-bold font-display text-slate-400 text-sm tracking-widest ml-1">CRM</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {allNavItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>

          {/* Alerts + Wellness + User section */}
          <div className="flex-shrink-0 p-4 border-t overflow-y-auto max-h-[45vh]" style={{ borderColor: 'var(--theme-border)' }}>
            {/* Alerts bell + Snippets */}
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[11px] font-medium theme-text-muted uppercase tracking-wider">Notificaciones</span>
              <div className="flex items-center gap-1">
                <SnippetsPanel userId={user?.user_id} />
                <AlertsDropdown />
              </div>
            </div>

            {/* Wellness widget */}
            <WellnessPanel />

            <div className="flex items-center gap-3 px-3 py-2 rounded-lg theme-bg-secondary mb-3" style={{ border: '1px solid var(--theme-border)' }}>
              <Avatar className="w-9 h-9">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-gradient-to-br from-cyan-400 to-blue-600 text-white text-sm">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium theme-text truncate">{user?.name}</p>
                <p className="text-xs theme-text-muted truncate">{user?.role === "admin" ? "Administrador" : "Usuario"}</p>
              </div>
            </div>

            {/* Theme Toggle */}
            <Button
              onClick={toggleTheme}
              variant="ghost"
              data-testid="theme-toggle-btn"
              className="w-full justify-start theme-text-secondary hover:text-teal-400 hover:bg-teal-400/10 mb-2"
            >
              {theme === "dark" ? (
                <>
                  <Sun className="w-4 h-4 mr-2" />
                  Modo claro
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 mr-2" />
                  Modo oscuro
                </>
              )}
            </Button>

            <Button
              onClick={handleLogout}
              variant="ghost"
              data-testid="logout-btn"
              className="w-full justify-start theme-text-secondary hover:text-red-400 hover:bg-red-400/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 theme-bg-secondary/90 backdrop-blur-xl px-4 py-3" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="theme-text-secondary"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <img
              src="/logo-light.png"
              alt="SRS CRM"
              className="h-8 w-auto"
            />
            <div className="flex items-center gap-2">
              <AlertsDropdown />
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-xs">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* Alert banner */}
        {alertCount > 0 && (
          <div className="mx-4 lg:mx-8 mt-4 mb-0">
            <button
              onClick={toggleDropdown}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg glass border border-teal-400/30 text-sm animate-fadeIn transition-all hover:border-teal-400/50"
            >
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-cyan-400" />
                <span className="theme-text-secondary">
                  Tienes <span className="text-cyan-400 font-medium">{alertCount}</span> {alertCount === 1 ? "alerta pendiente" : "alertas pendientes"}
                </span>
              </div>
              <span className="text-xs text-cyan-400 font-medium">Ver</span>
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
